/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon, ActionIconDropdown, DetailStack, Dropdown, FontWeight, HStack, ICON_EYE_OUTLINE, Popover, Text, UserPreview,
  SimpleTable,
} from '@doveit/bricks';
import { Rumor, Reminder, SerpRumor } from '../../../providers/api/dtos';

import ViewContactPopoverPreview from '../../../contact/containers/view-contact-popover-preview/ViewContactPopoverPreview';
import UpdateRumorStatus from '../update-rumor-status/UpdateRumorStatus';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { useCurrentAgentIsSameAgentOrManager } from '../../../agent/hooks/use-current-agent-is-same-agent-or-manager/useCurrentAgentIsSameAgentOrManager';
import ViewAgentPopoverPreview from '../../../agent/containers/view-agent-popover-preview/ViewAgentPopoverPreview';
import { ViewAgentSmallPreview } from '../../../agent/components';
import { toAgentPreviewProps } from '../../../agent/mappers/toAgentPreviewProps';
import ViewRumorPopoverPreview from '../view-rumor-popover-preview/ViewRumorPopoverPreview';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import RumorAvatar from '../../components/rumor-avatar/RumorAvatar';
import { ReferenceType, RumorStatus } from '../../../domain/types';
import { computeResolvableStatus } from '../../../utils/reminder/reminder';
import { ReferenceEntityWithNotes, ResolvableReminderStatus } from '../../../types';
import DateViewer from '../../../components/date-viewer/DateViewer';
import { goToDetailPage } from '../../../utils/navigate/utils';
import UpsertReminderAction from '../../../reminders/containers/upsert-reminder-action/UpsertReminderAction';
import SendWhatsappAction from '../../../containers/send-whatsapp-action/SendWhatsappAction';
import { rumorMessagesProvider } from '../../../containers/send-whatsapp-action/messages/rumorMessagesProvider';
import { updateRumor } from '../../../providers/api/rumor/rumorProvider';
import { buildWhatsappMessageSentNote } from '../../../intent/utils/buildWhatsappMessageSentNote';
import UpsertNoteAction from '../../../notes/containers/upsert-note-action-v2/UpsertNoteAction';
import * as styles from './ViewSerpRumorTableRow.styles';
import { reminderDateColor } from '../../utils';
import TextPreview from '../../../components/text-preview/TextPreview';

export interface ViewSerpRumorTableRowProps extends React.AriaAttributes {
  serpRumor: SerpRumor,
  onRumorUpdate?: (rumor: Rumor) => void,
  onReminderCreate?: (reminder: Reminder) => void,
}

const ViewSerpRumorTableRow: React.FC<ViewSerpRumorTableRowProps> = ({
  serpRumor,
  onRumorUpdate,
  onReminderCreate,
  ...rest
}) => {
  const {
    agent, contact, rumor, reminder,
  } = serpRumor;
  const {
    user, mainUserRole, userIsAdmin,
  } = useRBAC();
  const userIsRumorAgentOrManager = useCurrentAgentIsSameAgentOrManager(agent?.id);

  const reminderToCreate: Reminder = React.useMemo(() => ({
    agentId: agent.id,
    referenceId: rumor.id,
    referenceType: ReferenceType.RUMOR,
    resolved: false,
  } as Reminder), [agent.id, rumor.id]);

  const reminderStatus = React.useMemo(() => (reminder ? computeResolvableStatus(reminder) : undefined), [reminder]);

  const showReminder = React.useMemo(
    () => reminderStatus && [ResolvableReminderStatus.ACTIVE, ResolvableReminderStatus.EXPIRED].includes(reminderStatus),
    [reminderStatus],
  );

  const handleWhatsappSend = React.useCallback(async (message: string) => {
    try {
      const updatedRumor = await updateRumor(rumor.id!, {
        ...rumor,
        notes: [{
          text: buildWhatsappMessageSentNote(message),
          author: user.name,
          role: mainUserRole,
        }, ...(rumor.notes || [])],
      });

      onRumorUpdate?.(updatedRumor);
    } catch (_) { /* empty */ }
  }, [mainUserRole, onRumorUpdate, rumor, user.name]);

  const onNoteCreated = React.useCallback((updatedRumor: ReferenceEntityWithNotes) => {
    onRumorUpdate?.(updatedRumor as Rumor);
  }, [onRumorUpdate]);

  const goToRumorPage = React.useCallback(() => {
    goToDetailPage(rumor.id!, ReferenceType.RUMOR);
  }, [rumor.id]);

  const goToProspectPage = React.useCallback(() => {
    goToDetailPage(rumor.prospectId!, ReferenceType.PROSPECT);
  }, [rumor.prospectId]);

  return (
    <SimpleTable.Row {...rest}>
      <SimpleTable.Cell
        aria-label="Contatto"
        width="9rem"
      >
        {contact && (
          <ViewContactPopoverPreview
            contact={contact}
            trigger={(
              <div css={styles.userWrapper}>
                <UserPreview
                  name={contact.name || 'Contatto'}
                  size="S"
                >
                  {contact.phoneNumber || contact.email}
                </UserPreview>
              </div>
            )}
          />
        )}
        {!contact && (
          <UserPreview
            name="Non presente"
            size="S"
          />
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Notizia"
        width="12rem"
      >
        <ViewRumorPopoverPreview
          rumorId={rumor.id!}
          trigger={(
            <HStack gap={100} wrap="nowrap">
              <RumorAvatar size="S" />
              <Text.BodySmall
                aria-label="Indirizzo immobile"
                fontWeight={FontWeight.MEDIUM}
                css={setVerticalEllipsis()}
              >
                {rumor.propertyAddress || 'Non presente'}
              </Text.BodySmall>
            </HStack>
          )}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Stato della valutazione"
        width="9.5rem"
      >
        <UpdateRumorStatus
          rumor={rumor}
          size="XS"
          onSuccess={onRumorUpdate}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Agente"
        width="2rem"
      >
        <ViewAgentPopoverPreview
          agent={agent}
          trigger={(
            <ViewAgentSmallPreview
              {...toAgentPreviewProps(agent, { omit: { name: true } })}
              size="S"
              expanded
            />
          )}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Descrizione"
        withDivider
      >
        {rumor.description && (
          <TextPreview text={rumor.description} />
        )}
      </SimpleTable.Cell>
      <SimpleTable.Cell
        aria-label="Promemoria"
        width="8rem"
      >
        {showReminder && (
          <Popover
            aria-label="Visualizza dettagli promemoria"
            trigger={(
              <div style={{ maxWidth: '10rem' }}>
                <HStack gap={100} wrap="nowrap">
                  <div css={styles.dateWrapper}>
                    <DetailStack
                      label="Promemoria"
                      color={reminderDateColor(reminderStatus!)}
                    >
                      <DateViewer
                        stringDate={reminder!.startDate}
                        checkIfToday
                        checkIfTomorrow
                      />
                    </DetailStack>
                  </div>
                </HStack>
              </div>
            )}
            hasArrow
          >
            <div css={styles.notesPopoverWrapper}>
              <Text.BodySmall>{reminder!.notes}</Text.BodySmall>
            </div>
          </Popover>
        )}
      </SimpleTable.Cell>
      <SimpleTable.Cell
        aria-label="Azioni"
        align="right"
      >
        <HStack
          gap={75}
          wrap="nowrap"
        >
          <ActionIcon
            aria-label="Vai alla pagina della notizia"
            label="Visualizza"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToRumorPage}
            size="S"
          />
          {(userIsAdmin || userIsRumorAgentOrManager) && (
            <ActionIconDropdown
              aria-label="Visualizza azioni secondarie"
              label="Azioni"
              size="S"
              emphasis="low"
            >
              {rumor.prospectId && (
                <Dropdown.Option
                  label="Visualizza valutazione"
                  aria-label="Apri la pagina di valutazione"
                  onClick={goToProspectPage}
                />
              )}
              {contact?.phoneNumber && (
                <SendWhatsappAction
                  phoneNumber={contact.phoneNumber}
                  name={contact.name}
                  fetchMessages={rumorMessagesProvider(agent)}
                  onMessageSent={handleWhatsappSend}
                >
                  {({ onClick }) => (
                    <Dropdown.Option
                      label="Invia whatsapp"
                      onClick={onClick}
                    />
                  )}
                </SendWhatsappAction>
              )}
              {rumor.status === RumorStatus.IN_PROGRESS && (
                <UpsertReminderAction
                  reminder={reminderToCreate}
                  onSuccess={onReminderCreate}
                >
                  {({ upsert }) => (
                    <Dropdown.Option
                      label="Aggiungi promemoria"
                      onClick={upsert}
                    />
                  )}
                </UpsertReminderAction>
              )}
              <UpsertNoteAction
                reference={rumor}
                referenceType={ReferenceType.RUMOR}
                onSuccess={onNoteCreated}
              >
                {({ upsert }) => (
                  <Dropdown.Option
                    label="Aggiungi nota"
                    onClick={upsert}
                  />
                )}
              </UpsertNoteAction>
            </ActionIconDropdown>
          )}
        </HStack>
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default ViewSerpRumorTableRow;
